import React, { KeyboardEvent, useMemo, useRef } from "react";
import cn from "classnames";
import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { LIMIT_PAGE } from "Shared/Constants/app";
import SelectCheckbox from "./SelectCheckbox";
import { useFitToViewport } from "./useFitToViewport";
import { DataTableProps, RowId } from "./types";
import styles from "./DataTable.module.scss";

const SELECT_COLUMN_ID = "__select";
/** The header row's height (.headCell in DataTable.module.scss). */
const HEADER_HEIGHT = 40;

const defaultGetRowId = (row: any, index: number): RowId => row?.id ?? index;

const widthStyle = (width?: number | string) =>
  width === undefined
    ? undefined
    : { width, minWidth: typeof width === "number" ? width : undefined };

const SortIcon = ({ direction }: { direction: false | "asc" | "desc" }) => (
  <svg
    className={cn(styles.sortIcon, direction && styles.sortIcon_active)}
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    {direction === "asc" && <path d="M12 19V5m-6 6 6-6 6 6" />}
    {direction === "desc" && <path d="M12 5v14m-6-6 6 6 6-6" />}
    {!direction && <path d="m7 15 5 5 5-5M7 9l5-5 5 5" />}
  </svg>
);

function DataTable<T>({
  data,
  columns,
  variant = "page",
  density,
  getRowId = defaultGetRowId,
  onRowClick,
  selection,
  sorting,
  loading = false,
  // A full page of placeholders, so the table doesn't change height on load
  loadingRows = variant === "embedded" ? 6 : LIMIT_PAGE,
  error,
  onRetry,
  empty,
  headerOnly = false,
  toolbar,
  bulkActions,
  onClearSelection,
  footer,
  title,
  titleAction,
  maxHeight,
  fitToViewport,
  rowClassName,
  activeRowId,
  className,
  "aria-label": ariaLabel,
}: DataTableProps<T>) {
  const rows = Array.isArray(data) ? data : [];
  const rowIds = rows.map((row, index) => getRowId(row, index));
  const selectedOnPage = selection
    ? rowIds.filter((id) => selection.selected.has(id)).length
    : 0;
  const allSelected = rows.length > 0 && selectedOnPage === rows.length;
  const someSelected = selectedOnPage > 0 && !allSelected;

  const allColumns = useMemo<ColumnDef<T, any>[]>(() => {
    if (!selection) return columns;
    const selectColumn: ColumnDef<T, any> = {
      id: SELECT_COLUMN_ID,
      enableSorting: false,
      meta: { width: 44, interactive: true, align: "center" },
      header: () => (
        <SelectCheckbox
          aria-label={allSelected ? "Deselect all rows" : "Select all rows"}
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(checked) => selection.onToggleAll(checked)}
        />
      ),
      cell: ({ row }) => {
        const id = getRowId(row.original, row.index);
        return (
          <SelectCheckbox
            aria-label="Select row"
            checked={selection.selected.has(id)}
            onChange={(checked) => selection.onToggleRow(id, checked)}
          />
        );
      },
    };
    return [selectColumn, ...columns];
  }, [columns, selection, allSelected, someSelected]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const fittedHeight = useFitToViewport(
    scrollRef,
    maxHeight === undefined &&
      !headerOnly &&
      (fitToViewport ?? variant !== "embedded"),
  );
  const scrollHeight = maxHeight ?? fittedHeight;
  const refreshing = loading && rows.length > 0;

  const table = useReactTable<T>({
    data: rows,
    columns: allColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => String(getRowId(row, index)),
    manualSorting: true,
    enableSorting: !!sorting,
    enableMultiSort: false,
    state: sorting ? { sorting: sorting.state } : undefined,
    onSortingChange: sorting?.onChange,
  });

  const leafColumns = table.getVisibleLeafColumns();
  const hasFooterRow = columns.some((column) => column.footer !== undefined);
  const selectedCount = selection?.selected.size ?? 0;
  const showBulkBar = !!bulkActions && selectedCount > 0;
  const resolvedDensity =
    density ?? (variant === "page" ? "comfortable" : "compact");

  const alignClass = (column: Column<T, unknown>) => {
    const align = column.columnDef.meta?.align;
    return align === "right"
      ? styles.alignRight
      : align === "center"
        ? styles.alignCenter
        : undefined;
  };

  const onRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick?.(row);
    }
  };

  const renderBody = () => {
    if (headerOnly) return null;
    if (loading && rows.length === 0) {
      return Array.from({ length: loadingRows }, (_, index) => (
        <tr key={`skeleton-${index}`} className={styles.row} aria-hidden="true">
          {leafColumns.map((column) => (
            <td key={column.id} className={styles.cell}>
              {column.id === SELECT_COLUMN_ID ? null : (
                <span
                  className={styles.skeleton}
                  style={{
                    width: `${45 + ((index * 17 + column.getIndex() * 23) % 45)}%`,
                  }}
                />
              )}
            </td>
          ))}
        </tr>
      ));
    }

    if (error || rows.length === 0) {
      return (
        <tr>
          <td colSpan={leafColumns.length} className={styles.stateCell}>
            {error ? (
              <div
                className={cn(styles.state, styles.state_error)}
                role="alert"
              >
                <svg
                  className={styles.stateIcon}
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5M12 16h.01" />
                </svg>
                <p className={styles.stateTitle}>{error}</p>
                {onRetry && (
                  <button
                    type="button"
                    className={styles.stateButton}
                    onClick={onRetry}
                  >
                    Try again
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.state} role="status">
                <svg
                  className={styles.stateIcon}
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <rect x="3" y="4" width="18" height="16" rx="2" />
                  <path d="M3 10h18" />
                </svg>
                <p className={styles.stateTitle}>
                  {empty?.title ?? "No records found"}
                </p>
                {empty?.description && (
                  <p className={styles.stateDescription}>{empty.description}</p>
                )}
                {empty?.action}
              </div>
            )}
          </td>
        </tr>
      );
    }

    return table.getRowModel().rows.map((row) => {
      const id = getRowId(row.original, row.index);
      const isSelected = !!selection?.selected.has(id);
      const isActive = activeRowId !== undefined && activeRowId === id;
      return (
        <tr
          key={row.id}
          className={cn(
            styles.row,
            onRowClick && styles.row_clickable,
            isSelected && styles.row_selected,
            isActive && styles.row_active,
            rowClassName?.(row.original),
          )}
          onClick={onRowClick ? () => onRowClick(row.original) : undefined}
          onKeyDown={
            onRowClick
              ? (event) => onRowKeyDown(event, row.original)
              : undefined
          }
          tabIndex={onRowClick ? 0 : undefined}
          aria-selected={selection ? isSelected : undefined}
          aria-current={isActive ? "true" : undefined}
        >
          {row.getVisibleCells().map((cell) => {
            const meta = cell.column.columnDef.meta;
            const content = flexRender(
              cell.column.columnDef.cell,
              cell.getContext(),
            );
            return (
              <td
                key={cell.id}
                className={cn(
                  styles.cell,
                  alignClass(cell.column),
                  meta?.truncate && styles.cell_truncate,
                  meta?.cellClassName,
                )}
                style={widthStyle(meta?.width)}
                onClick={
                  meta?.interactive
                    ? (event) => event.stopPropagation()
                    : undefined
                }
              >
                {meta?.truncate ? (
                  <div
                    className={styles.truncate}
                    title={
                      typeof cell.getValue() === "string"
                        ? (cell.getValue() as string)
                        : undefined
                    }
                  >
                    {content}
                  </div>
                ) : (
                  content
                )}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  return (
    <div
      className={cn(
        styles.root,
        styles[`root_${variant}`],
        styles[`density_${resolvedDensity}`],
        className,
      )}
      style={
        {
          // The empty and error states take the skeleton's height (capped to
          // the fitted scroll area), so switching between them doesn't jump.
          "--dt-loading-rows": loadingRows,
          ...(typeof scrollHeight === "number" && {
            "--dt-state-max": `${scrollHeight - HEADER_HEIGHT}px`,
          }),
        } as React.CSSProperties
      }
    >
      {(title || titleAction) && (
        <div className={styles.titleBar}>
          <div className={styles.title}>{title}</div>
          {titleAction}
        </div>
      )}
      {toolbar && <div className={styles.toolbar}>{toolbar}</div>}
      {showBulkBar && (
        <div className={styles.bulkBar} role="region" aria-label="Bulk actions">
          <span className={styles.bulkCount}>{selectedCount} selected</span>
          <span className={styles.bulkDivider} />
          <div className={styles.bulkActions}>{bulkActions(selectedCount)}</div>
          {onClearSelection && (
            <button
              type="button"
              className={styles.bulkClear}
              aria-label="Clear selection"
              onClick={onClearSelection}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          )}
        </div>
      )}
      <div
        ref={scrollRef}
        className={cn(styles.scroll, refreshing && styles.scroll_refreshing)}
        style={
          scrollHeight !== undefined ? { maxHeight: scrollHeight } : undefined
        }
        aria-busy={loading}
      >
        <table className={styles.table} aria-label={ariaLabel}>
          <thead className={styles.head}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta;
                  const canSort = header.column.getCanSort();
                  const direction = header.column.getIsSorted();
                  const label = header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      );
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className={cn(
                        styles.headCell,
                        alignClass(header.column),
                        direction && styles.headCell_sorted,
                        meta?.headerClassName,
                      )}
                      style={widthStyle(meta?.width)}
                      aria-sort={
                        direction === "asc"
                          ? "ascending"
                          : direction === "desc"
                            ? "descending"
                            : canSort
                              ? "none"
                              : undefined
                      }
                    >
                      {canSort ? (
                        <button
                          type="button"
                          className={styles.sortButton}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {label}
                          <SortIcon direction={direction} />
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>{renderBody()}</tbody>
          {hasFooterRow && rows.length > 0 && !error && (
            <tfoot className={styles.foot}>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <td
                      key={header.id}
                      className={cn(
                        styles.cell,
                        styles.footCell,
                        alignClass(header.column),
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </td>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
      {/* The pagination counts belong to the last good load, not the error;
          the footer stays mounted (hidden) so the table keeps its height. */}
      {footer && (
        <div
          className={cn(styles.footer, error && styles.footer_hidden)}
          aria-hidden={error ? true : undefined}
        >
          {footer}
        </div>
      )}
    </div>
  );
}

export default DataTable;
